import * as React from "react";
import { PanelLeftOpen, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { useSignOut } from "react-firebase-hooks/auth";
import { useDownloadURL } from "react-firebase-hooks/storage";
import { ref } from "firebase/storage";
import { storage } from "@/config/firebaseConfig";

import { NavMain, navRowClassName, type NavMainItem } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import { BrandBlock } from "@/components/shell/BrandBlock";
import { AnserMark } from "@/components/ui/anser-mark";
import { OrgScopeSwitcher } from "@/components/sidebar/OrgScopeSwitcher";
import { ProductSwitcher } from "@/components/sidebar/ProductSwitcher";
import { SidebarAskRow } from "@/components/sidebar/SidebarAskRow";
import { SidebarCreateAdsCta } from "@/components/sidebar-create-ads-cta";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { auth } from "@/config/firebaseConfig";
import { useProfile } from "@/context/ProfileContext";
import { useProductScope } from "@/context/ProductScopeContext";
import { useRemoteConfigBoolean } from "@/context/RemoteConfigContext";
import { useMemberAvatarUrl } from "@/hooks/use-member-avatar-url";
import { useVisibleSidebarNavFeatures } from "@/hooks/use-sidebar-nav-features";
import {
  getActiveSidebarFeatureId,
  isFeatureSubtreeActive,
  SIDEBAR_NAV_GROUPS,
  type SidebarNavFeature,
} from "@/config/sidebar-nav";

/**
 * Exactly one row lights: the feature that owns the longest prefix of the
 * current path, sub-pages included (`getActiveSidebarFeatureId`), so the
 * Anser chat (`/coach`) stays dark on `/coach/teams` and Team stays lit on
 * `/people` and `/coach/members/:id`.
 */
function featuresToNavItems(
  features: SidebarNavFeature[],
  activeId: string | undefined,
  t: TFunction<"nav">,
): NavMainItem[] {
  return features.map((feature) => ({
    title: t(feature.titleKey),
    url: feature.url ?? feature.children?.[0]?.url ?? "#",
    icon: feature.icon,
    titleBadge: feature.titleBadge,
    isActive: feature.id === activeId,
  }));
}

/**
 * The 248 px sidebar from the platform sketch: the brand block, the six
 * Coach groups (Idag, Agera, Mål, Rutiner, Team, Anser), the "Mer" heading
 * with Underlag, and Inställningar pinned at the bottom. The workspace
 * switch, the bell and the account row live in the desktop top bar; the
 * phone sheet keeps them here. Recruit and Leads render their own rows under
 * the same brand. Tokens only.
 */
export default function AppSidebar({
  className,
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const { t: tNav } = useTranslation("nav");
  const { memberProfile, businessProfile } = useProfile();
  const { state, isMobile, setOpen } = useSidebar();
  const [signOut] = useSignOut(auth);

  // When collapsed, clicking any empty region of the rail expands the sidebar.
  // Clicks that land on interactive elements (nav icon buttons, avatar menu,
  // team switcher, etc.) keep their own behavior and are not swallowed.
  const handleCollapsedRailClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (state !== "collapsed" || isMobile) return;
      const target = event.target as HTMLElement;
      if (
        target.closest(
          'a, button, input, select, textarea, [role="button"], [role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"], [role="switch"], [role="tab"]',
        )
      ) {
        return;
      }
      setOpen(true);
    },
    [state, isMobile, setOpen],
  );

  const avatarUrl = useMemberAvatarUrl(memberProfile?.uid);

  const [businessLogoUrl] = useDownloadURL(
    businessProfile?.uid && isMobile
      ? ref(
          storage,
          `businessUsersAvatars/${businessProfile.uid}/profile_image`,
        )
      : undefined,
  );

  const showIntelligence = useRemoteConfigBoolean("show_intelligence");
  const visibleFeatures = useVisibleSidebarNavFeatures();
  const { activeProduct } = useProductScope();

  // Flat groups: Idag first (ungrouped), then the *active* product's own
  // group only (GRA-78) — Coach, Recruit and Leads are separate workspaces,
  // never shown mixed together. Settings is kept in the config for title
  // lookups but rendered pinned in the footer.
  const mainFeatures = visibleFeatures.filter((f) => !f.hiddenFromNav);
  const activeId = getActiveSidebarFeatureId(mainFeatures, location.pathname);
  const toNavItems = (features: SidebarNavFeature[]) =>
    featuresToNavItems(features, activeId, tNav);
  // Idag is ungrouped in the config, but it only works for Coach - Recruit
  // and Leads get their own landing pages - so it's hidden rather than shown
  // broken outside the Coach workspace (GRA-78).
  const todayItems = toNavItems(
    mainFeatures.filter(
      (f) => !f.group && (f.id !== "overview" || activeProduct === "coach"),
    ),
  );
  // The Coach group: the main rows, then the rows under the "Mer" heading.
  const coachFeatures = mainFeatures.filter((f) => f.group === "coach");
  const coachItems =
    activeProduct === "coach"
      ? toNavItems(coachFeatures.filter((f) => !f.more))
      : [];
  const moreItems =
    activeProduct === "coach"
      ? toNavItems(coachFeatures.filter((f) => f.more))
      : [];
  const productGroups = SIDEBAR_NAV_GROUPS.filter(
    (group) => group === activeProduct && group !== "coach",
  )
    .map((group) => ({
      group,
      items: toNavItems(mainFeatures.filter((f) => f.group === group)),
    }))
    .filter(({ items }) => items.length > 0);

  // The footer Settings row is stable across every product (GRA-78) and
  // stays behind every Settings child in the nav config (including /apps,
  // which lives outside the /settings prefix).
  const settingsFeature = visibleFeatures.find((f) => f.id === "settings");
  const settingsActive = settingsFeature
    ? isFeatureSubtreeActive(settingsFeature, location.pathname)
    : false;

  if (!memberProfile) {
    return (
      <Sidebar collapsible="icon" className={className} {...props}>
        <SidebarHeader className="gap-0 px-4 pt-7">
          <BrandBlock />
        </SidebarHeader>
        <SidebarContent>
          <div className="space-y-3 p-4">
            <Skeleton className="h-10 w-full rounded-g-md" />
            <Skeleton className="h-10 w-full rounded-g-md" />
            <Skeleton className="h-10 w-full rounded-g-md" />
            <Skeleton className="mt-4 h-4 w-12" />
            <Skeleton className="h-10 w-full rounded-g-md" />
          </div>
        </SidebarContent>
        <SidebarFooter>
          <div className="flex items-center gap-2 p-2">
            <Skeleton className="size-8 rounded-lg" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        </SidebarFooter>
      </Sidebar>
    );
  }

  const userForNav = {
    name: memberProfile.name ?? "",
    email: memberProfile.email ?? "",
    avatar: avatarUrl,
  };

  return (
    <Sidebar
      collapsible="icon"
      className={cn(
        className,
        "font-g",
        // Signal that clicking the collapsed rail re-expands the sidebar,
        // matching the SidebarRail's own e-resize (right arrow) cursor. Real
        // interactive controls keep a pointer cursor so they still read as
        // directly clickable rather than as expand affordances.
        "group-data-[collapsible=icon]:cursor-e-resize",
        "group-data-[collapsible=icon]:[&_a]:cursor-pointer group-data-[collapsible=icon]:[&_button]:cursor-pointer group-data-[collapsible=icon]:[&_[role=button]]:cursor-pointer",
      )}
      onClick={handleCollapsedRailClick}
      {...props}
    >
      <SidebarHeader className="gap-0 px-4 pt-7 group-data-[collapsible=icon]:px-2">
        <div className="group-data-[collapsible=icon]:hidden">
          <BrandBlock />
        </div>
        {/*
          Collapsed rail: the mark crossfades to an "Open sidebar" button
          while the cursor is anywhere inside the sidebar (group-hover). The
          button is also keyboard-focusable and reveals on focus-visible.
        */}
        <div className="relative mx-auto hidden h-[46px] w-[46px] group-data-[collapsible=icon]:block">
          <AnserMark
            size={46}
            className="absolute inset-0 transition-opacity duration-200 group-hover:opacity-0"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={tNav("sidebar.open")}
            onClick={() => setOpen(true)}
            className="absolute inset-0 h-[46px] w-[46px] opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-visible:opacity-100"
          >
            <PanelLeftOpen />
          </Button>
        </div>
        {isMobile ? (
          <div className="pt-5">
            <OrgScopeSwitcher
              orgName={businessProfile?.business ?? "Graftus"}
              orgLogoUrl={businessLogoUrl}
              isLoading={!businessProfile}
            />
            {showIntelligence ? (
              <div className="mt-2">
                <SidebarAskRow />
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="pt-4 empty:hidden">
          <ProductSwitcher />
        </div>
        {/* The "create" shortcuts belong to Recruit and Leads; the Coach
            workspace asks Anser instead. */}
        {activeProduct !== "coach" ? <SidebarCreateAdsCta /> : null}
      </SidebarHeader>
      <SidebarContent className="px-4 pt-3 group-data-[collapsible=icon]:px-2">
        <NavMain
          items={[...todayItems, ...coachItems]}
          pathname={location.pathname}
        />
        {moreItems.length > 0 ? (
          <NavMain
            items={moreItems}
            pathname={location.pathname}
            groupLabel={tNav("sections.more")}
          />
        ) : null}
        {productGroups.map(({ group, items }) => (
          <NavMain
            key={group}
            items={items}
            pathname={location.pathname}
            groupLabel={tNav(`groups.${group}`)}
          />
        ))}
      </SidebarContent>
      <SidebarFooter className="px-4 pb-5 group-data-[collapsible=icon]:px-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={tNav("settings")}
              isActive={settingsActive}
              className={navRowClassName}
            >
              <Link
                to="/settings"
                aria-current={settingsActive ? "page" : undefined}
              >
                <Settings aria-hidden="true" />
                <span>{tNav("settings")}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        {/* The account row lives in the desktop top bar; keep it for the
            phone sheet and the collapsed icon rail. */}
        <div className={cn(!isMobile && "lg:hidden group-data-[collapsible=icon]:block")}>
          <NavUser user={userForNav} onSignOut={() => void signOut()} />
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
